'use client';

import LoadingLink from '@/components/ui/LoadingLink/LoadingLink';
import NextImageView from '@/components/ui/NextImageView/NextImageView';
import RoundArrowIcon from '@/components/ui/RoundArrowIcon/RoundArrowIcon';
import RoundTextIcon from '@/components/ui/RoundTextIcon/RoundTextIcon';
import SpeechBubbleTextIcon from '@/components/ui/SpeechBubbleTextIcon/SpeechBubbleTextIcon';
import { formatWorkDate } from '@/features/gfWorks/utils/functions';
import { useGlobalLoadingAtom } from '@/state';
import { BadgeStatus, type GfWork } from '@/types';
import { formatCreatorNames } from '@/utils/formatters';
import { getImageLayoutSizes } from '@/utils/nextImage';
import type { Route } from 'next';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import styles from './WorkList.module.scss';

type WorkListProps = {
  works: GfWork[];
  workLinkPrefix: string;
  workLinkParam?: Record<string, string>;
};

function WorkList({ works, workLinkPrefix = '/works', workLinkParam = {} }: WorkListProps) {
  const [hoveredWork, setHoveredWork] = useState<GfWork | null>(works.length > 0 ? works[0] : null);
  const router = useRouter();
  const [_, startGlobalLoading] = useGlobalLoadingAtom();
  const handleWorkItemHover = (work: GfWork | null): void => {
    setHoveredWork(work);
  };
  const getRouteForWork = (workId: number) => {
    const route = `${workLinkPrefix}/${workId}`;
    // クエリパラメータがある場合は追加
    if (Object.keys(workLinkParam).length > 0) {
      const queryString = new URLSearchParams(workLinkParam).toString();
      return `${route}?${queryString}` as Route;
    }
    return route as Route;
  };

  return (
    <div className={styles.workListContainer}>
      <div className={styles.listSection}>
        {works.map((work) => (
          <LoadingLink
            href={getRouteForWork(work.id)}
            key={work.id}
            data-testid={`work-list-item-${work.id}-${work.name.replace(/[\s\-]/g, '')}`}
          >
            <div className={styles.workItem} onMouseEnter={() => handleWorkItemHover(work)}>
              <div className={styles.workItemThumbnail}>
                <NextImageView
                  containerClassName={styles.workItemThumbnailContainer}
                  className={styles.workItemThumbnailImg}
                  src={work.thumbnailPath}
                  alt={work.name}
                  fill={false}
                  width={50}
                  height={50}
                  objectFit='cover'
                />
              </div>
              <div className={styles.workItemTitle}>
                <span>{work.name}</span>
                <div className={styles.badgeStatusWrapper}>
                  {work.badgeStatus === BadgeStatus.new.value && (
                    <RoundTextIcon
                      bgColor={BadgeStatus[work.badgeStatus].color}
                      text={BadgeStatus[work.badgeStatus].text}
                      size='xSmall'
                    />
                  )}
                  {work.badgeStatus === BadgeStatus.added.value && (
                    <SpeechBubbleTextIcon
                      bgColor={BadgeStatus[work.badgeStatus].color}
                      text={BadgeStatus[work.badgeStatus].text}
                      size='xSmall'
                      direction='left'
                    />
                  )}
                </div>
              </div>
              <p className={styles.workItemAuthor}>{formatCreatorNames(work.creators)}</p>
              <p className={styles.workItemDate}>
                {formatWorkDate(work.startDate, work.startIssueNumber)}
                &nbsp;〜&nbsp;
                {formatWorkDate(work.endDate, work.endIssueNumber)}
              </p>
              <div className={styles.workItemIcon}>
                <RoundArrowIcon color='red' direction='right' />
              </div>
            </div>
          </LoadingLink>
        ))}
      </div>
      <div className={styles.imageSection}>
        {hoveredWork ? (
          <>
            <div
              className={styles.imageSectionImage}
              onClick={() => {
                const route = getRouteForWork(hoveredWork.id);
                startGlobalLoading();
                router.push(route);
              }}
            >
              <NextImageView
                src={hoveredWork.imagePath}
                alt='ホバー中の作品画像'
                fill
                sizes={getImageLayoutSizes({ sp: '90vw', pc: '210px' })}
                objectFit='contain'
              />
            </div>
            <p className={styles.imageSectionWorkName}>{hoveredWork.name}</p>
            <p className={styles.imageSectionWorkAuthor}>{formatCreatorNames(hoveredWork.creators)}</p>
            <p className={styles.imageSectionWorkDate}>
              {formatWorkDate(hoveredWork.startDate, hoveredWork.startIssueNumber)}
              &nbsp;〜&nbsp;
              {formatWorkDate(hoveredWork.endDate, hoveredWork.endIssueNumber)}
            </p>
          </>
        ) : (
          <></>
        )}
      </div>
    </div>
  );
}

export default WorkList;
